import { useEffect, useRef, useState } from 'react';

import type { ThinkingStep } from '../types';
import { isFinished } from '../utils/messageParts';

const elapsedSeconds = (from: number) => (Date.now() - from) / 1000;

type ThinkingTimings = {
  total: number | undefined;
  perStep: Record<string, number>;
  elapsed: number;
};

const TICK_MS = 1000;

/**
 * Wall-clock timings for a turn's thinking: per-step durations, the turn total and a live
 * elapsed count, all timed from `turnStartedAt`. A block that never turns active (a thread
 * reopened from history) stays untimed rather than showing 0s.
 */
export const useThinkingTimings = (
  steps: ThinkingStep[],
  active: boolean,
  turnStartedAt: number | undefined
): ThinkingTimings => {
  const runningSince = useRef(new Map<string, number>());
  const timedFrom = useRef<number | undefined>(undefined);
  const latestSteps = useRef(steps);
  const [perStep, setPerStep] = useState<Record<string, number>>({});
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [elapsed, setElapsed] = useState(0);

  latestSteps.current = steps;
  const signature = steps.map(step => `${step.id}:${step.state}`).join('|');

  useEffect(() => {
    if (active && timedFrom.current === undefined) {
      timedFrom.current = turnStartedAt;
    }

    const finished: Record<string, number> = {};
    latestSteps.current.forEach(step => {
      const startedAt = runningSince.current.get(step.id);
      if (startedAt === undefined) {
        if (!isFinished(step)) runningSince.current.set(step.id, Date.now());
        return;
      }
      if (isFinished(step)) {
        finished[step.id] = elapsedSeconds(startedAt);
        runningSince.current.delete(step.id);
      }
    });

    if (Object.keys(finished).length) {
      setPerStep(current => ({ ...current, ...finished }));
    }
  }, [signature, active, turnStartedAt]);

  useEffect(() => {
    if (!active && timedFrom.current !== undefined) {
      setTotal(elapsedSeconds(timedFrom.current));
    }
  }, [active]);

  useEffect(() => {
    const startedAt = timedFrom.current;
    if (!active || startedAt === undefined) return undefined;
    const tick = () => setElapsed(Math.floor(elapsedSeconds(startedAt)));
    // a block that mounts mid-turn would otherwise show 0 until the first tick
    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [active]);

  return { total, perStep, elapsed };
};
